import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../components/Breadcrumbs";
import InputField from "../components/InputField";
import Button from "../components/Button";
import api from "../api/api";
import { Modal, Box } from "@mui/material";
import Loader from "../components/Loader";
 
const ViewWarehouseDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
 
  const [warehouseData, setWarehouseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [rejectReason, setRejectReason] = useState("");
 
  useEffect(() => {
    const fetchRequestDetails = async () => {
      try {
        const response = await api.get(`/warehouse/add-wh-requests/${id}`);
        setWarehouseData(response.data.request);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchRequestDetails();
  }, [id]);
 
  const handleApproveClick = () => {
    setShowConfirmModal(true);
  };
 
  const handleConfirmApprove = async () => {
    setVerifying(true);
    try {
      await api.post(`/warehouse/approve-request/${id}`);
      setSuccessMessage("Warehouse request approved successfully!");
      setShowConfirmModal(false);
      setShowSuccessModal(true);
    } catch (err) {
      console.error(err);
      alert("Failed to approve request");
    } finally {
      setVerifying(false);
    }
  };
 
  const handleCancelApprove = () => {
    setShowConfirmModal(false);
  };
 
  const handleRejectClick = () => {
    setShowRejectModal(true);
  };
 
  const handleConfirmReject = async () => {
    if (!rejectReason.trim()) {
      alert("Please provide a rejection reason");
      return;
    }
 
    setRejecting(true); // start loader
 
    try {
      await api.post(`/warehouse/reject-request/${id}`, {
        rejection_reason: rejectReason,
      });
      setSuccessMessage("Warehouse request rejected successfully!");
      setShowRejectModal(false); // close after success
      setShowSuccessModal(true);
    } catch (err) {
      console.error(err);
      alert("Failed to reject request");
    } finally {
      setRejecting(false);
    }
  };
 
  const handleCancelReject = () => {
    setShowRejectModal(false);
    setRejectReason("");
  };
 
  if (loading || !warehouseData) {
    return <div className="container mt-3">Loading...</div>;
  }
 
  return (
    <>
      <Breadcrumbs />
 
      <div className="container mt-3">
        <div className="card shadow-sm mb-4">
          <div
            className="card-header"
            style={{ background: "#1d3160", color: "#fff" }}
          >
            Warehouse Details
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <InputField
                  label="Warehouse Name"
                  value={warehouseData.warehouse_name}
                  readOnly
                />
              </div>
 
              <div className="col-md-6">
                <InputField
                  label="Location/City"
                  value={warehouseData.location_city}
                  readOnly
                />
              </div>
 
              <div className="col-md-3">
                <InputField
                  label="Base Rate"
                  type="number"
                  value={warehouseData.base_rate}
                  readOnly
                />
              </div>
 
              <div className="col-md-3">
                <InputField
                  label="Pincode"
                  value={warehouseData.pincode}
                  readOnly
                />
              </div>
 
              <div className="col-md-6">
                <InputField
                  label="Address Line 1"
                  value={warehouseData.address1}
                  readOnly
                />
              </div>
 
              <div className="col-md-6">
                <InputField
                  label="Address Line 2"
                  value={warehouseData.address2}
                  readOnly
                />
              </div>
            </div>
          </div>
        </div>
 
        <div className="card shadow-sm mb-4">
          <div
            className="card-header"
            style={{ background: "#1d3160", color: "#fff" }}
          >
            Warehouse Manager Details
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-4">
                <InputField
                  label="Manager Name"
                  value={warehouseData.manager_name}
                  readOnly
                />
              </div>
 
              <div className="col-md-4">
                <InputField
                  label="Manager Email"
                  value={warehouseData.manager_email}
                  readOnly
                />
              </div>
 
              <div className="col-md-4">
                <InputField
                  label="Manager Contact"
                  value={warehouseData.manager_contact}
                  readOnly
                />
              </div>
            </div>
          </div>
        </div>
 
        <div className="d-flex justify-content-end gap-3 mb-5">
          <Button
            text={verifying ? "Approving" : "Approve"}
            btncls="btn-sm px-3 d-flex align-items-center"
            style={{ backgroundColor: "#28a745", borderColor: "#28a745" }}
            onClick={handleApproveClick}
            disabled={verifying}
            title="Approve"
          />
 
          <Button
            text={rejecting ? "Rejecting" : "Reject"}
            btncls="btn-sm px-3 d-flex align-items-center"
            style={{ backgroundColor: "#ff6060", borderColor: "#ff6060" }}
            onClick={handleRejectClick}
            disabled={rejecting}
            title="Reject"
          />
        </div>
 
        {showConfirmModal && (
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Approval</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCancelApprove}
                  ></button>
                </div>
                <div className="modal-body">
                  <p>
                    Are you sure you want to approve this warehouse request?
                  </p>
                  <p>
                    This will create a warehouse manager account and warehouse
                    entry.
                  </p>
                </div>
                <div className="modal-footer">
                  <Button
                    text="Cancel"
                    className="btn btn-secondary"
                    onClick={handleCancelApprove}
                    disabled={verifying}
                  />
 
                  <Button
                    text={
                      verifying ? (
                        <>
                          <Loader /> Confirming...
                        </>
                      ) : (
                        "Confirm"
                      )
                    }
                    className="btn btn-primary"
                    onClick={handleConfirmApprove}
                    disabled={verifying}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
 
        {showRejectModal && (
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Reject Warehouse Request</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCancelReject}
                  ></button>
                </div>
 
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="rejectReason" className="form-label">
                      Rejection Reason
                    </label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Enter reason for rejection"
                    />
                  </div>
                </div>
 
                <div className="modal-footer">
                  <Button
                    text="Cancel"
                    className="btn btn-secondary"
                    onClick={handleCancelReject}
                    disabled={rejecting}
                  />
 
                  <Button
                    text={
                      rejecting ? (
                        <>
                          <Loader /> Rejecting...
                        </>
                      ) : (
                        "Reject"
                      )
                    }
                    className="btn btn-primary"
                    onClick={handleConfirmReject}
                    disabled={rejecting}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
 
        <Modal
          open={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
        >
          <Box className="modal-box" style={{ height: "200px" }}>
            <h6 className="fw-bold">Success</h6>
            <p>{successMessage}</p>
            <div className="row mt-5 justify-content-center">
              <div className="col-6">
                <Button
                  variant="primary"
                  btncls="w-100"
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate("/warehouse-add-requests");
                  }}
                >
                  OK
                </Button>
              </div>
            </div>
          </Box>
        </Modal>
      </div>
    </>
  );
};
 
export default ViewWarehouseDetails;